/* eslint-disable import/no-duplicates */
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { Chart } from 'react-google-charts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useCurrentEffect } from '../../../../../../lib/use-current-effect';

import { usePatient } from '../../../../../../hooks/patient';
import { getMeasurementsByDay } from '../../../../../../services/metrics';

import MeasurementsArea from '../../MeasurementsArea';
import { ChildrenProps, ChildrenRefProps, DataChildrenProps } from '../..';
import { MetricTypeEnum } from '../../../../../../enums/MetricTypeEnum';

type GlucoseData = [string, number];

const GlucoseChart: React.ForwardRefRenderFunction<
  ChildrenRefProps,
  ChildrenProps
> = ({ dateFilter }, ref) => {
  const { patient } = usePatient();

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<DataChildrenProps>();
  const [glucoseGraphData, setGlucoseGraphData] = useState<GlucoseData[]>([
    ['00:00', 0],
  ]);

  const handleChange = useCallback(
    async (patientId?: string, isCurrent = () => true) => {
      setIsLoading(true);
      if (!patientId) {
        setIsLoading(false);
        return;
      }
      const response = await getMeasurementsByDay({
        patientId,
        metricType: MetricTypeEnum.GLUCOSE,
        dateFilter,
      });
      if (response.length > 0) {
        const lastRecordTime = format(
          new Date(response[response.length - 1].mensuramentDate),
          "dd'/'MM'/'yyyy', ' HH'h'mm",
          { locale: ptBR },
        );

        const result: GlucoseData[] = response.map(
          ({ glucose, mensuramentDate }) => [
            format(new Date(mensuramentDate), 'HH:mm', {
              locale: ptBR,
            }),
            glucose,
          ],
        );

        const value = result[result.length - 1][1].toString();

        if (isCurrent()) {
          setGlucoseGraphData(result);
          setData({
            lastRecordTime,
            value,
          });
        }
      } else if (isCurrent()) {
        setGlucoseGraphData([['00:00', 0]]);
        setData(undefined);
      }

      setIsLoading(false);
    },
    [dateFilter],
  );

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          handleChange(patient.id, isCurrent);
        } else {
          setIsLoading(false);
        }
      })();
    },
    [dateFilter, handleChange, patient],
  );

  useImperativeHandle(ref, () => ({
    reload: () => handleChange(patient?.id),
  }));

  const valuesDataGlucose = glucoseGraphData.map((item) => item[1]);
  const tickMin = Math.min(...valuesDataGlucose);
  const tickMax = Math.max(...valuesDataGlucose);

  const differenceValueMinMax = tickMax - tickMin;

  const intervalValueMinMax = differenceValueMinMax / 4;
  const roundedInternal = Math.ceil(intervalValueMinMax);

  const generateTicks = [0, 1, 2, 3, 4].map((multipleFactor) => {
    return tickMin + roundedInternal * multipleFactor;
  });

  return (
    <MeasurementsArea
      data={{
        styleData: 'chart',
        isLoading,
        cardTitle: 'mg/dL',
        dataDetails: data
          ? {
              title: 'último registro:',
              position: 'center',
              lastRecordTime: data.lastRecordTime,
              type: 'mg/dL',
              value: data.value,
              inAlert: false,
            }
          : undefined,
      }}
    >
      <Chart
        width="110%"
        height="100%"
        chartType="LineChart"
        style={{
          marginLeft: '-10px',
        }}
        options={{
          legend: 'none',
          colors: ['#2D9CDB'],
          vAxis: {
            ticks: generateTicks,
          },
          series: {
            0: { curveType: 'function' },
          },
        }}
        data={[['h', 'mg/dL'], ...glucoseGraphData]}
        rootProps={{ 'data-testid': '2' }}
      />
    </MeasurementsArea>
  );
};

export default forwardRef(GlucoseChart);
